// @file Composable for publishing post drafts
import { isPostEmpty } from '@@/bits/post_properties'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceGuidedTemplateStore } from '@@/pinia/surface_guided_template_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Cid, DraftPost } from '@@/types'
import { storeToRefs } from 'pinia'
import type { Ref } from 'vue'
import { computed } from 'vue'

export const usePostDraftPublisher = ({
  draftCid,
}: {
  draftCid: Ref<Cid>
}): {
  canAutoPublishDraft: typeof canAutoPublishDraft
  shouldDisableSave: typeof shouldDisableSave
  publishCurrentDraft: typeof publishCurrentDraft
} => {
  const { isContainerSmallerThanTabletPortrait } = storeToRefs(useSurfaceContainerSizeStore())
  const { isMap } = storeToRefs(useSurfaceStore())
  const { canIPost } = storeToRefs(useSurfacePermissionsStore())

  const surfaceDraftsStore = useSurfaceDraftsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceGuidedTemplateStore = useSurfaceGuidedTemplateStore()

  const { draftByCid } = storeToRefs(surfaceDraftsStore)
  const currentDraft = computed<DraftPost | undefined>(() => draftByCid.value[draftCid.value])

  const { isExistingPost } = surfacePostsStore
  const isCurrentDraftNewPost = computed<boolean>(() => !isExistingPost(draftCid.value))

  const canAutoPublishDraft = computed<boolean>(() => {
    return currentDraft.value?.uploadingFile != null && !isContainerSmallerThanTabletPortrait.value
  })

  const shouldDisableSave = computed<boolean>(() => {
    if (!canIPost.value) return true
    const isMapPostWithoutLocation =
      isMap.value &&
      (typeof currentDraft.value?.location_point?.longitude !== 'number' ||
        typeof currentDraft.value?.location_point?.latitude !== 'number')
    return (
      (isPostEmpty(currentDraft.value) && !canAutoPublishDraft.value) ||
      (isMapPostWithoutLocation && isCurrentDraftNewPost.value) ||
      currentDraft.value?.hasCustomPropertyErrors === true
    )
  })

  const { isDiscussionBoard } = storeToRefs(surfaceGuidedTemplateStore)
  const { togglePanelMinimizationState } = surfaceGuidedTemplateStore
  const { publishDraft } = surfaceDraftsStore

  const publishCurrentDraft = (): void => {
    if (isDiscussionBoard.value) {
      togglePanelMinimizationState()
    }
    publishDraft({ cid: draftCid.value })
  }

  return { canAutoPublishDraft, shouldDisableSave, publishCurrentDraft }
}
